import React, { useState } from "react";
import { RxCross2 } from "react-icons/rx";
import { showToast } from "src/utils/toast.util";
import {
  AICoachPromptType,
  UpsertAICoachPromptPayload,
} from "../entities/ai-coach-prompt.entity";
import { useUpsertAICoachPromptsMutation } from "../ai-coach-prompts.service";

type Props = {
  isOpen: boolean;
  onClose: () => void;
};

type ParsedPromptPreview = UpsertAICoachPromptPayload & {
  preview: string;
};

const getIndent = (line: string) => line.match(/^ */)?.[0].length || 0;

const normalizeBlock = (lines: string[], minimumIndent: number) => {
  const meaningfulLines = lines.filter((line) => line.trim() !== "");
  const effectiveIndent = meaningfulLines.length
    ? Math.min(...meaningfulLines.map((line) => Math.max(getIndent(line), minimumIndent)))
    : minimumIndent;

  return lines
    .map((line) => {
      if (!line.trim()) return "";
      return line.slice(Math.min(effectiveIndent, line.length));
    })
    .join("\n")
    .replace(/\n+$/, "");
};

const collectIndentedBlock = (lines: string[], startIndex: number, minimumIndent: number) => {
  const blockLines: string[] = [];
  let index = startIndex;

  while (index < lines.length) {
    const currentLine = lines[index];
    const trimmed = currentLine.trim();

    if (!trimmed) {
      blockLines.push("");
      index += 1;
      continue;
    }

    if (getIndent(currentLine) < minimumIndent) break;

    blockLines.push(currentLine);
    index += 1;
  }

  return {
    value: normalizeBlock(blockLines, minimumIndent),
    nextIndex: index,
  };
};

const humanizePromptSegment = (segment: string) =>
  segment
    .split(/[_\-.]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(" ");

const getPromptType = (promptKey: string): AICoachPromptType => {
  if (promptKey === "CUSTOM_SCENARIO_PROMPT") return "CUSTOM_SCENARIO";
  if (promptKey.startsWith("TOPICS.") || promptKey.startsWith("TOPIC.")) return "TOPIC";
  return "SYSTEM";
};

const buildTitle = (promptKey: string) => {
  if (promptKey === "CUSTOM_SCENARIO_PROMPT") return "Custom Scenario Prompt";
  if (promptKey === "LIVE_COACH_SYSTEM_PROMPT_BEGINNER") return "Beginner System Prompt";
  if (promptKey === "LIVE_COACH_SYSTEM_PROMPT_STANDARD") return "Standard System Prompt";

  const segments = promptKey.split(".").filter(Boolean);
  const targetSegment = segments[segments.length - 1] || promptKey;
  return humanizePromptSegment(targetSegment);
};

const getPromptPreview = (prompt: string) =>
  prompt
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 160);

const parseYamlPrompts = (text: string): ParsedPromptPreview[] => {
  const lines = text.replace(/\r\n/g, "\n").split("\n");
  const mappedPrompts: UpsertAICoachPromptPayload[] = [];
  const stack: Array<{ indent: number; key: string }> = [];
  let index = 0;

  const addPrompt = (promptKey: string, currentPrompt: string) => {
    if (!currentPrompt.trim()) return;

    const promptType = getPromptType(promptKey);
    mappedPrompts.push({
      promptKey,
      promptType,
      title: buildTitle(promptKey),
      currentPrompt,
      requestStatus: "IDLE",
    });
  };

  while (index < lines.length) {
    const line = lines[index];
    const trimmed = line.trim();

    if (!trimmed || trimmed.startsWith("#")) {
      index += 1;
      continue;
    }

    const keyMatch = line.match(/^(\s*)([^:#][^:]*):\s*(\|)?\s*$/);
    if (!keyMatch) {
      index += 1;
      continue;
    }

    const indent = keyMatch[1].length;
    const key = keyMatch[2].trim();
    const isBlockPrompt = Boolean(keyMatch[3]);

    while (stack.length && stack[stack.length - 1].indent >= indent) {
      stack.pop();
    }

    const promptKey = [...stack.map((item) => item.key), key].join(".");

    if (isBlockPrompt) {
      const block = collectIndentedBlock(lines, index + 1, indent + 2);
      addPrompt(promptKey, block.value);
      index = block.nextIndex;
      continue;
    }

    if (trimmed.endsWith(":")) {
      stack.push({ indent, key });
      index += 1;
      continue;
    }

    index += 1;
  }

  const dedupedPrompts = mappedPrompts.filter((prompt, currentIndex, allPrompts) => {
    return (
      allPrompts.findIndex((candidate) => candidate.promptKey === prompt.promptKey) === currentIndex
    );
  });

  if (!dedupedPrompts.length) {
    throw new Error("No valid prompts were found in the YAML file.");
  }

  return dedupedPrompts.map((prompt) => ({
    ...prompt,
    preview: getPromptPreview(prompt.currentPrompt),
  }));
};

export const ImportPromptsYamlModal: React.FC<Props> = ({ isOpen, onClose }) => {
  const [upsertPrompts, { isLoading }] = useUpsertAICoachPromptsMutation();
  const [fileName, setFileName] = useState("");
  const [yamlText, setYamlText] = useState("");
  const [parsedPrompts, setParsedPrompts] = useState<ParsedPromptPreview[]>([]);
  const [parseError, setParseError] = useState("");

  if (!isOpen) return null;

  const resetState = () => {
    setFileName("");
    setYamlText("");
    setParsedPrompts([]);
    setParseError("");
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const content = await file.text();
      const mapped = parseYamlPrompts(content);

      setFileName(file.name);
      setYamlText(content);
      setParsedPrompts(mapped);
      setParseError("");
    } catch (error) {
      setFileName(file.name);
      setYamlText("");
      setParsedPrompts([]);
      setParseError((error as Error).message || "Failed to parse the YAML file.");
    }
  };

  const handleImport = async () => {
    if (!parsedPrompts.length) {
      showToast("warrning", "Please choose a valid YAML file first.");
      return;
    }

    try {
      await upsertPrompts(
        parsedPrompts.map(({ preview, ...prompt }) => prompt)
      ).unwrap();

      showToast("success", `Imported ${parsedPrompts.length} prompts successfully.`);
      handleClose();
    } catch (error) {
      showToast("error", "Failed to import prompts into the database.");
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/70 p-4 backdrop-blur-sm">
      <div className="relative max-h-[92vh] w-full max-w-6xl overflow-hidden rounded-[28px] border border-slate-200 bg-slate-50 shadow-2xl">
        <div className="flex items-start justify-between gap-6 border-b border-slate-200 bg-white px-6 py-5">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.24em] text-cyan-700">YAML Import</p>
            <h2 className="mt-1 text-2xl font-semibold text-slate-900">Import AI Coach Prompts</h2>
            <p className="mt-2 text-sm text-slate-600">
              Upload the YAML file and push its prompts into <span className="font-semibold text-slate-900">ai_coach_prompts</span>.
            </p>
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="rounded-full border border-slate-200 bg-white p-2 text-slate-500 transition-colors hover:border-slate-300 hover:text-slate-700"
            aria-label="Close modal"
          >
            <RxCross2 className="text-xl" />
          </button>
        </div>

        <div className="max-h-[calc(92vh-92px)] overflow-y-auto px-6 py-6">
          <div className="grid gap-6 xl:grid-cols-[380px_minmax(0,1fr)]">
            <section className="space-y-4 rounded-[28px] border border-slate-200 bg-white p-5 shadow-sm">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500">Upload file</p>
                <h3 className="mt-1 text-lg font-semibold text-slate-950">YAML source</h3>
              </div>

              <label className="block rounded-[24px] border-2 border-dashed border-slate-300 bg-slate-50 p-6 text-center transition-colors hover:border-slate-400 hover:bg-slate-100">
                <input
                  type="file"
                  accept=".yaml,.yml,text/yaml,text/x-yaml"
                  onChange={handleFileChange}
                  className="hidden"
                />
                <p className="text-sm font-semibold text-slate-900">Choose YAML file</p>
                <p className="mt-2 text-xs text-slate-600">Accepts the prompt format with system keys, custom scenario, and TOPICS mapping.</p>
              </label>

              <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
                <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Selected file</p>
                <p className="mt-2 text-sm text-slate-900">{fileName || "No file selected"}</p>
              </div>

              <div className="flex flex-col gap-3 border-t border-slate-200 pt-4">
                <button
                  type="button"
                  onClick={handleImport}
                  disabled={isLoading || !parsedPrompts.length}
                  className="rounded-full bg-slate-950 px-5 py-3 text-sm font-semibold text-white shadow-lg shadow-slate-950/15 transition-all hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {isLoading ? "Importing..." : "Import prompts to database"}
                </button>
                <button
                  type="button"
                  onClick={handleClose}
                  className="rounded-full border border-slate-300 bg-white px-5 py-3 text-sm font-semibold text-slate-700 transition-colors hover:border-slate-400 hover:bg-slate-100"
                >
                  Cancel
                </button>
              </div>
            </section>

            <section className="space-y-4 rounded-[28px] border border-slate-200 bg-white shadow-sm">
              <div className="border-b border-slate-200 px-5 py-4">
                <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500">Import preview</p>
                <h3 className="mt-1 text-lg font-semibold text-slate-950">Mapped prompt documents</h3>
              </div>

              <div className="space-y-4 px-5 pb-5">
                {parseError ? (
                  <div className="mt-5 rounded-2xl border border-red-200 bg-red-50 px-4 py-4 text-sm text-red-800">
                    {parseError}
                  </div>
                ) : null}

                {yamlText ? (
                  <div className="mt-5 rounded-[24px] border border-slate-200 bg-slate-950">
                    <div className="border-b border-white/10 px-4 py-3">
                      <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">Source YAML</p>
                    </div>
                    <pre className="max-h-[220px] overflow-auto whitespace-pre-wrap px-4 py-4 font-mono text-[12px] leading-6 text-cyan-50">
                      {yamlText}
                    </pre>
                  </div>
                ) : null}

                <div className="space-y-3">
                  {parsedPrompts.length ? (
                    parsedPrompts.map((prompt) => (
                      <div key={prompt.promptKey} className="rounded-[24px] border border-slate-200 bg-slate-50 p-4">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="rounded-full border border-slate-200 bg-white px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-slate-700">
                            {prompt.promptType}
                          </span>
                          <span className="font-mono text-[11px] text-slate-500">{prompt.promptKey}</span>
                        </div>
                        <p className="mt-3 text-sm font-semibold text-slate-950">{prompt.title}</p>
                        <p className="mt-2 text-sm leading-6 text-slate-600">{prompt.preview}</p>
                      </div>
                    ))
                  ) : (
                    <div className="mt-5 rounded-[24px] border border-dashed border-slate-300 bg-slate-50 px-5 py-10 text-center">
                      <p className="text-base font-semibold text-slate-900">No import preview yet</p>
                      <p className="mt-2 text-sm text-slate-600">Choose a YAML file to preview the prompts before importing them.</p>
                    </div>
                  )}
                </div>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};
